import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { AccessTimeOutlined } from '@mui/icons-material';

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

const FULL_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'long',
};

// Poppins has no tabular figures, so digits vary in width and the clock
// jitters as it ticks. Render each digit in a fixed 1ch slot instead.
const FixedWidthTime = ({ value }: { value: string }) => (
  <>
    {value.split('').map((char, index) => (
      <Box
        key={index}
        component="span"
        sx={
          /\d/.test(char)
            ? { display: 'inline-block', width: '1ch', textAlign: 'center' }
            : undefined
        }
      >
        {char}
      </Box>
    ))}
  </>
);

const HeaderClock = () => {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatters = useMemo(
    () => ({
      utcTime: new Intl.DateTimeFormat('en-GB', {
        ...TIME_FORMAT,
        timeZone: 'UTC',
      }),
      localTime: new Intl.DateTimeFormat('en-GB', TIME_FORMAT),
      utcFull: new Intl.DateTimeFormat('en-GB', {
        ...FULL_FORMAT,
        timeZone: 'UTC',
      }),
      localFull: new Intl.DateTimeFormat('en-GB', FULL_FORMAT),
      localZoneName: new Intl.DateTimeFormat('en-GB', {
        timeZoneName: 'short',
      }),
    }),
    [],
  );

  const localZoneAbbr = formatters.localZoneName
    .formatToParts(now)
    .find(part => part.type === 'timeZoneName')?.value;
  const localIsUtc = now.getTimezoneOffset() === 0;

  return (
    <Tooltip
      title={
        <>
          System time (UTC): {formatters.utcFull.format(now)}
          <br />
          Your local time: {formatters.localFull.format(now)}
        </>
      }
    >
      <Box
        data-testid="header-clock"
        aria-label="System time"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <AccessTimeOutlined
          sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }}
        />
        <Typography
          component="span"
          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
          data-testid="header-clock-utc"
        >
          <FixedWidthTime value={formatters.utcTime.format(now)} /> UTC
        </Typography>
        {!localIsUtc && (
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Typography
              component="span"
              sx={{ mx: 0.75, color: 'text.secondary', fontSize: '0.75rem' }}
            >
              ·
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
              data-testid="header-clock-local"
            >
              <FixedWidthTime value={formatters.localTime.format(now)} />{' '}
              {localZoneAbbr}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default HeaderClock;
